import React, { useEffect, useRef, useState } from 'react';
import { getLocations } from '../../services/inventoryCorrections.service';
import {
  DashboardOverview,
  getDashboardOverview,
} from '../../services/dashboard.service';
import { useResponsive } from '../../hook/useResponsive';

const TEAL = '#0F7679';
const TEAL_DARK = '#05292A';
const BG = '#F4F8F9';
const BORDER = '#E5ECEC';
const MUTED = '#6B7787';
const POSITIVE_BG = '#E6F6EE';
const POSITIVE_TEXT = '#0E8B53';
const NEGATIVE_BG = '#FDECEC';
const NEGATIVE_TEXT = '#C53030';
const AMBER = '#F59E0B';
const CARD_SHADOW =
  '0 1px 2px rgba(5,41,42,0.04), 0 8px 24px rgba(5,41,42,0.05)';

const REFRESH_MS = 60_000;

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-DE').format(Number(value || 0));
const formatAxisMoney = (value: number) => {
  if (value >= 1000)
    return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  return `${value.toFixed(0)}`;
};
const dateLocale = () =>
  localStorage.getItem('language') === 'al' ? 'sq-AL' : 'en-GB';
const formatDay = (iso: string) => {
  if (!iso) return '';
  const date = new Date(`${iso}T00:00:00.000Z`);
  return date.toLocaleDateString(dateLocale(), {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
};

const emptyOverview: DashboardOverview = {
  date: '',
  live: {
    revenue: 0,
    txnCount: 0,
    covers: 0,
    refunds: 0,
    refundCount: 0,
    discounts: 0,
    voids: 0,
    cash: { total: 0, hasCashTender: false },
    hourly: [],
    topItems: [],
    now: { tillsOnline: 0, tillsTotal: 0, openSessions: 0, pendingOrders: 0 },
  },
  context: {
    sameWeekdayLastWeek: { date: '', revenue: 0, txnCount: 0 },
    typicalHourly: [],
    weekToDate: { from: '', revenue: 0, txnCount: 0 },
  },
};

const deltaPct = (value: number, prev: number) =>
  prev === 0 ? (value === 0 ? 0 : 100) : ((value - prev) / prev) * 100;

const Dashboard = () => {
  const { isMobile } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const [data, setData] = useState<DashboardOverview>(emptyOverview);
  const [locations, setLocations] = useState<any[]>([]);
  const [locationId, setLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locationRef = useRef(locationId);
  locationRef.current = locationId;
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const t = (key: string) => language?.[key] ?? key;

  const load = async (background = false) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    try {
      const response: any = await getDashboardOverview(
        locationRef.current || undefined,
      );
      setData(response?.data?.data ?? emptyOverview);
      setError(null);
    } catch (loadError) {
      console.error('Error loading dashboard:', loadError);
      if (!background) setError(t('Unable to load the dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, [locationId]);

  // Quiet minute-by-minute refresh; skipped while the tab is hidden.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden) return;
      load(true);
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response: any = await getLocations(userId, companyId, 1, 100, '');
        const list =
          response?.data?.data?.locations ?? response?.data?.data ?? [];
        setLocations(Array.isArray(list) ? list : []);
      } catch (locationsError) {
        console.error('Error loading locations:', locationsError);
      }
    };
    loadLocations();
  }, []);

  const { live, context } = data;
  const revenueDelta = deltaPct(
    live.revenue,
    context.sameWeekdayLastWeek.revenue,
  );

  const kpiCards: {
    key: string;
    title: string;
    value: string;
    sub?: string;
    icon: React.ReactNode;
    accent: string;
    tone?: 'bad';
  }[] = [
    {
      key: 'txns',
      title: t('Transactions'),
      value: formatNumber(live.txnCount),
      sub:
        live.covers > 0
          ? `${formatNumber(live.covers)} ${t('covers')} · ${formatMoney(live.covers ? live.revenue / live.covers : 0)} / ${t('cover')}`
          : live.txnCount > 0
            ? `${formatMoney(live.txnCount ? live.revenue / live.txnCount : 0)} / ${t('transaction')}`
            : undefined,
      icon: <IconActivity />,
      accent: '#0EA5E9',
    },
    ...(live.cash.hasCashTender || live.cash.total > 0
      ? [
          {
            key: 'cash',
            title: t('Cash in drawers'),
            value: formatMoney(live.cash.total),
            sub: t('Cash tenders today'),
            icon: <IconCash />,
            accent: AMBER,
          },
        ]
      : []),
    {
      key: 'refunds',
      title: t('Refunds today'),
      value: formatMoney(live.refunds),
      sub: `${formatNumber(live.refundCount)} ${t('refunds')}`,
      icon: <IconRefund />,
      accent: '#EF4444',
      tone: live.refunds > 0 ? ('bad' as const) : undefined,
    },
    {
      key: 'discounts',
      title: t('Discounts today'),
      value: formatMoney(live.discounts),
      icon: <IconTag />,
      accent: '#8B5CF6',
    },
    {
      key: 'voids',
      title: t('Voids today'),
      value: formatNumber(live.voids),
      icon: <IconVoid />,
      accent: MUTED,
      tone: live.voids > 0 ? ('bad' as const) : undefined,
    },
  ];

  return (
    <div
      className="h-full overflow-auto font-inter"
      style={{
        color: TEAL_DARK,
        backgroundColor: BG,
        backgroundImage:
          'radial-gradient(1200px 400px at -10% -20%, rgba(15,118,121,0.08), transparent 60%), radial-gradient(900px 320px at 110% -10%, rgba(59,130,246,0.06), transparent 60%)',
        padding: isMobile ? '16px' : '24px',
      }}
    >
      {/* ---- header ---- */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div
            className="inline-flex items-center rounded-full"
            style={{
              backgroundColor: 'rgba(15,118,121,0.08)',
              color: TEAL,
              fontSize: '12px',
              fontWeight: 600,
              gap: '8px',
              padding: '6px 14px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '999px',
                backgroundColor: refreshing ? AMBER : TEAL,
                display: 'inline-block',
              }}
            />
            {t('Live')}
          </div>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 700,
              marginTop: '10px',
              letterSpacing: '-0.02em',
            }}
          >
            {t('Dashboard')}
          </h1>
          <p style={{ fontSize: '14px', color: MUTED, marginTop: '6px' }}>
            {formatDay(data.date)}
            <span style={{ color: '#94A3B8' }}>
              {'  ·  '}
              {t('All figures in EUR')}
              {'  ·  '}
              {t('Updates every minute')}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t('Location')}
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            className="rounded-xl"
            style={{
              backgroundColor: '#fff',
              border: `1px solid ${BORDER}`,
              padding: '9px 12px',
              fontSize: '13px',
              fontWeight: 500,
              color: TEAL_DARK,
              minWidth: '150px',
              boxShadow: CARD_SHADOW,
            }}
          >
            <option value="">{t('All locations')}</option>
            {locations.map((location: any) => (
              <option key={location?.id} value={location?.id}>
                {location?.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 transition"
            style={{
              backgroundColor: TEAL_DARK,
              color: '#fff',
              fontSize: '13px',
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'wait' : 'pointer',
              boxShadow: '0 6px 16px rgba(5,41,42,0.18)',
            }}
          >
            {loading || refreshing ? t('Refreshing') : t('Refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mt-4 flex items-center gap-2 rounded-xl px-4 py-3"
          style={{
            backgroundColor: NEGATIVE_BG,
            color: NEGATIVE_TEXT,
            fontSize: '13px',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {/* ---- right now strip ---- */}
      <div
        className="grid mt-5"
        style={{
          gap: '12px',
          gridTemplateColumns: isMobile
            ? 'repeat(auto-fit, minmax(150px, 1fr))'
            : 'repeat(3, minmax(0, 1fr))',
        }}
      >
        <NowChip
          label={t('Tills online')}
          value={`${live.now.tillsOnline} / ${live.now.tillsTotal}`}
          ok={
            live.now.tillsTotal === 0 ||
            live.now.tillsOnline === live.now.tillsTotal
          }
          loading={loading}
        />
        <NowChip
          label={t('Open sessions')}
          value={formatNumber(live.now.openSessions)}
          ok
          loading={loading}
        />
        <NowChip
          label={t('Pending take-away orders')}
          value={formatNumber(live.now.pendingOrders)}
          ok={live.now.pendingOrders === 0}
          warn={live.now.pendingOrders > 0}
          loading={loading}
        />
      </div>

      {/* ---- hero: revenue today ---- */}
      <div
        className="mt-4 rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${TEAL_DARK} 0%, #0B4D4F 55%, ${TEAL} 100%)`,
          color: '#fff',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 18px 48px rgba(5,41,42,0.18)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '420px',
            height: '100%',
            background:
              'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.18), transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <div
          className="flex flex-wrap items-end justify-between gap-6"
          style={{ position: 'relative' }}
        >
          <div>
            <p
              style={{
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {t('Revenue today')}
            </p>
            {loading ? (
              <Skeleton height="44px" width="220px" tone="dark" />
            ) : (
              <p
                style={{
                  fontSize: '40px',
                  fontWeight: 700,
                  marginTop: '8px',
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatMoney(live.revenue)}
              </p>
            )}
            <div
              className="flex flex-wrap items-center gap-3"
              style={{ marginTop: '12px' }}
            >
              <DeltaPill deltaPct={revenueDelta} dark />
              <span
                style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}
              >
                {t('vs')} {formatMoney(context.sameWeekdayLastWeek.revenue)}
                <span style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {' '}
                  · {formatDay(context.sameWeekdayLastWeek.date)}
                </span>
              </span>
            </div>
          </div>

          <div style={{ minWidth: isMobile ? '100%' : '240px' }}>
            <div style={{ width: '100%', height: '72px' }}>
              <Sparkline
                values={live.hourly.map((point) => point.revenue)}
                stroke="#A7F3D0"
                fill="rgba(167,243,208,0.18)"
              />
            </div>
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.65)',
                textAlign: isMobile ? 'left' : 'right',
                marginTop: '6px',
              }}
            >
              {t('Week to date')}{' '}
              <span style={{ color: '#fff', fontWeight: 600 }}>
                {formatMoney(context.weekToDate.revenue)}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* ---- today KPIs ---- */}
      <div
        className="grid mt-5"
        style={{
          gap: '16px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}
      >
        {kpiCards.map((card) => (
          <div
            key={card.key}
            className="rounded-2xl"
            style={{
              backgroundColor: '#fff',
              border: `1px solid ${BORDER}`,
              padding: '18px',
              boxShadow: CARD_SHADOW,
              minHeight: '120px',
            }}
          >
            <div className="flex items-start justify-between">
              <div
                className="flex items-center justify-center rounded-xl"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: `${card.accent}14`,
                  color: card.accent,
                }}
              >
                {card.icon}
              </div>
              {card.tone === 'bad' && (
                <span
                  className="rounded-full"
                  style={{
                    backgroundColor: NEGATIVE_BG,
                    color: NEGATIVE_TEXT,
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '4px 10px',
                  }}
                >
                  {t('Check')}
                </span>
              )}
            </div>
            {loading ? (
              <Skeleton height="32px" width="70%" />
            ) : (
              <>
                <p
                  style={{
                    fontSize: '13px',
                    color: MUTED,
                    fontWeight: 500,
                    marginTop: '12px',
                  }}
                >
                  {card.title}
                </p>
                <p
                  style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    marginTop: '2px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {card.value}
                </p>
                {card.sub && (
                  <p
                    style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}
                  >
                    {card.sub}
                  </p>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* ---- hourly curve + top items ---- */}
      <div
        className="grid mt-5"
        style={{
          gap: '16px',
          gridTemplateColumns: isMobile
            ? 'minmax(0, 1fr)'
            : 'minmax(0, 2fr) minmax(280px, 1fr)',
        }}
      >
        <HourlyChart
          t={t}
          loading={loading}
          today={live.hourly}
          typical={context.typicalHourly}
          isMobile={isMobile}
        />
        <TopItems t={t} loading={loading} items={live.topItems} />
      </div>
    </div>
  );
};

const NowChip = ({
  label,
  value,
  ok,
  warn,
  loading,
}: {
  label: string;
  value: string;
  ok?: boolean;
  warn?: boolean;
  loading: boolean;
}) => (
  <div
    className="flex items-center justify-between rounded-2xl"
    style={{
      backgroundColor: '#fff',
      border: `1px solid ${BORDER}`,
      padding: '12px 16px',
      boxShadow: CARD_SHADOW,
      gap: '12px',
    }}
  >
    <span className="flex items-center" style={{ gap: '10px', minWidth: 0 }}>
      <span
        aria-hidden
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '999px',
          flex: 'none',
          backgroundColor: warn ? AMBER : ok ? POSITIVE_TEXT : NEGATIVE_TEXT,
        }}
      />
      <span
        style={{
          fontSize: '13px',
          color: MUTED,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </span>
    <span
      style={{
        fontSize: '16px',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        flex: 'none',
      }}
    >
      {loading ? '—' : value}
    </span>
  </div>
);

const HourlyChart = ({
  t,
  loading,
  today,
  typical,
  isMobile,
}: {
  t: (key: string) => string;
  loading: boolean;
  today: { hour: number; revenue: number }[];
  typical: { hour: number; revenue: number }[];
  isMobile?: boolean;
}) => {
  const width = 720;
  const height = 260;
  const padX = 40;
  const padY = 24;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  // Trim to the venue's active window so quiet 3am hours don't flatten service.
  const active = Array.from({ length: 24 }, (_, hour) => hour).filter(
    (hour) =>
      (today[hour]?.revenue ?? 0) > 0 || (typical[hour]?.revenue ?? 0) > 0,
  );
  const fromHour = active.length ? Math.max(0, active[0] - 1) : 8;
  const toHour = active.length
    ? Math.min(23, active[active.length - 1] + 1)
    : 22;
  const hours = Array.from(
    { length: toHour - fromHour + 1 },
    (_, i) => fromHour + i,
  );
  const maxValue = Math.max(
    1,
    ...hours.map((hour) =>
      Math.max(today[hour]?.revenue ?? 0, typical[hour]?.revenue ?? 0),
    ),
  );
  const slot = innerW / hours.length;
  const barW = Math.min(26, slot * 0.55);
  const xFor = (i: number) => padX + i * slot + slot / 2;
  const yFor = (value: number) => padY + innerH - (value / maxValue) * innerH;

  const typicalPath = hours
    .map(
      (hour, i) =>
        `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)} ${yFor(typical[hour]?.revenue ?? 0).toFixed(2)}`,
    )
    .join(' ');

  const yTicks = 4;
  const tickValues = Array.from(
    { length: yTicks + 1 },
    (_, i) => (maxValue * (yTicks - i)) / yTicks,
  );
  const totalToday = today.reduce((sum, point) => sum + point.revenue, 0);
  const hasData = active.length > 0;

  return (
    <section
      className="rounded-2xl"
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        padding: '20px',
        boxShadow: CARD_SHADOW,
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p style={{ fontSize: '16px', fontWeight: 700 }}>
            {t('Today by hour')}
          </p>
          <p style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
            {t('Revenue per hour against a typical week')}
          </p>
        </div>
        <div
          className="flex items-center gap-4"
          style={{ fontSize: '12px', color: MUTED }}
        >
          <LegendDot
            color={TEAL}
            label={`${t('Today')} · ${formatMoney(totalToday)}`}
          />
          <LegendDot color={AMBER} label={t('Typical (last 4 weeks)')} dashed />
        </div>
      </div>

      {loading ? (
        <Skeleton height="260px" />
      ) : !hasData ? (
        <EmptyState label={t('No sales yet today')} />
      ) : (
        <div style={{ marginTop: '16px', overflowX: 'auto' }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: '100%',
              minWidth: isMobile ? 0 : '480px',
              height: isMobile ? 'auto' : '260px',
              maxHeight: '300px',
              display: 'block',
            }}
          >
            {tickValues.map((value, idx) => {
              const y = padY + (idx / yTicks) * innerH;
              return (
                <g key={`grid-${idx}`}>
                  <line
                    x1={padX}
                    x2={width - padX}
                    y1={y}
                    y2={y}
                    stroke={BORDER}
                    strokeDasharray="3 4"
                  />
                  <text
                    x={padX - 8}
                    y={y + 4}
                    fontSize="10"
                    fill={MUTED}
                    textAnchor="end"
                  >
                    {formatAxisMoney(value)}
                  </text>
                </g>
              );
            })}

            {hours.map((hour, i) => {
              const value = today[hour]?.revenue ?? 0;
              const y = yFor(value);
              return (
                <rect
                  key={`bar-${hour}`}
                  x={xFor(i) - barW / 2}
                  y={y}
                  width={barW}
                  height={Math.max(0, padY + innerH - y)}
                  rx={4}
                  fill={TEAL}
                  fillOpacity={value > 0 ? 0.9 : 0.15}
                >
                  <title>{`${String(hour).padStart(2, '0')}:00 · ${formatMoney(value)}`}</title>
                </rect>
              );
            })}

            <path
              d={typicalPath}
              fill="none"
              stroke={AMBER}
              strokeWidth="2"
              strokeDasharray="5 5"
              strokeLinejoin="round"
            />

            {hours.map((hour, i) =>
              hour % (hours.length > 14 ? 3 : 2) === 0 ? (
                <text
                  key={`label-${hour}`}
                  x={xFor(i)}
                  y={height - 4}
                  fontSize="10"
                  fill={MUTED}
                  textAnchor="middle"
                >
                  {String(hour).padStart(2, '0')}
                </text>
              ) : null,
            )}
          </svg>
        </div>
      )}
    </section>
  );
};

const TopItems = ({
  t,
  loading,
  items,
}: {
  t: (key: string) => string;
  loading: boolean;
  items: {
    productId: string;
    productName: string;
    quantity: number;
    revenue: number;
  }[];
}) => (
  <section
    className="rounded-2xl"
    style={{
      backgroundColor: '#fff',
      border: `1px solid ${BORDER}`,
      padding: '20px',
      boxShadow: CARD_SHADOW,
    }}
  >
    <p style={{ fontSize: '16px', fontWeight: 700 }}>{t('Top items today')}</p>
    <p style={{ fontSize: '12px', color: MUTED, marginTop: '2px' }}>
      {t('By revenue, sales only')}
    </p>

    {loading ? (
      <Skeleton height="220px" />
    ) : items.length === 0 ? (
      <EmptyState label={t('No sales yet today')} />
    ) : (
      <div className="space-y-3" style={{ marginTop: '16px' }}>
        {items.map((item, index) => {
          const maxRevenue = items[0]?.revenue || 1;
          return (
            <div key={item.productId}>
              <div className="flex items-center justify-between gap-3">
                <div
                  className="flex items-center"
                  style={{ gap: '10px', minWidth: 0 }}
                >
                  <span
                    className="flex items-center justify-center rounded-lg"
                    style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: index === 0 ? `${TEAL}14` : BG,
                      color: index === 0 ? TEAL : MUTED,
                      fontSize: '12px',
                      fontWeight: 700,
                      flex: 'none',
                    }}
                  >
                    {index + 1}
                  </span>
                  <p
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.productName}
                  </p>
                </div>
                <p
                  style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    flex: 'none',
                  }}
                >
                  {formatMoney(item.revenue)}
                </p>
              </div>
              <div
                style={{
                  marginTop: '6px',
                  width: '100%',
                  height: '5px',
                  borderRadius: '999px',
                  backgroundColor: BG,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, (item.revenue / maxRevenue) * 100)}%`,
                    height: '100%',
                    backgroundColor: TEAL,
                    borderRadius: '999px',
                    opacity: 1 - index * 0.15,
                  }}
                />
              </div>
              <p style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>
                {formatNumber(item.quantity)} {t('sold')}
              </p>
            </div>
          );
        })}
      </div>
    )}
  </section>
);

const DeltaPill = ({
  deltaPct,
  dark,
}: {
  deltaPct: number;
  dark?: boolean;
}) => {
  const positive = deltaPct >= 0;
  const bg = dark
    ? positive
      ? 'rgba(16,185,129,0.18)'
      : 'rgba(248,113,113,0.22)'
    : positive
      ? POSITIVE_BG
      : NEGATIVE_BG;
  const color = dark
    ? positive
      ? '#A7F3D0'
      : '#FECACA'
    : positive
      ? POSITIVE_TEXT
      : NEGATIVE_TEXT;
  return (
    <span
      className="inline-flex items-center rounded-full"
      style={{
        backgroundColor: bg,
        color,
        fontSize: '12px',
        fontWeight: 700,
        padding: '5px 12px',
        gap: '6px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        style={{ flexShrink: 0 }}
      >
        {positive ? (
          <path d="M5 1.5l3 3.5H6.2v3H3.8v-3H2L5 1.5z" fill="currentColor" />
        ) : (
          <path d="M5 8.5l-3-3.5h1.8v-3h2.4v3H8L5 8.5z" fill="currentColor" />
        )}
      </svg>
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
};

const Sparkline = ({
  values,
  stroke,
  fill,
}: {
  values: number[];
  stroke: string;
  fill?: string;
}) => {
  if (!values || values.length < 2) {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
      >
        <line
          x1="0"
          x2="100"
          y1="20"
          y2="20"
          stroke={stroke}
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  const w = 100;
  const h = 30;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values.map((value, idx) => {
    const x = (idx / (values.length - 1)) * w;
    const y = h - ((value - min) / range) * h;
    return [x, y] as const;
  });
  const lineD = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  const areaD = `${lineD} L${w} ${h} L0 ${h} Z`;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
    >
      {fill && <path d={areaD} fill={fill} />}
      <path
        d={lineD}
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

const Skeleton = ({
  height,
  width,
  tone = 'light',
}: {
  height: string;
  width?: string;
  tone?: 'light' | 'dark';
}) => (
  <div
    className="animate-pulse rounded-xl"
    style={{
      height,
      width: width ?? '100%',
      backgroundColor: tone === 'dark' ? 'rgba(255,255,255,0.12)' : '#EEF3F4',
      marginTop: '12px',
    }}
  />
);

const EmptyState = ({ label }: { label: string }) => (
  <div
    className="flex flex-col items-center justify-center rounded-xl"
    style={{
      marginTop: '16px',
      padding: '32px 16px',
      color: MUTED,
      backgroundColor: BG,
      border: `1px dashed ${BORDER}`,
    }}
  >
    <p style={{ fontSize: '13px' }}>{label}</p>
  </div>
);

const LegendDot = ({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) => (
  <span className="inline-flex items-center gap-2">
    <span
      style={{
        width: '20px',
        height: '0',
        borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
        display: 'inline-block',
      }}
    />
    <span style={{ fontSize: '12px', color: MUTED, fontWeight: 500 }}>
      {label}
    </span>
  </span>
);

const IconActivity = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 12h4l3-8 4 16 3-8h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconCash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="7"
      width="18"
      height="11"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle cx="12" cy="12.5" r="2.6" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const IconRefund = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 14l-4-4 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 10h9a5 5 0 0 1 0 10h-3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);
const IconTag = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 4h7l9 9-7 7-9-9V4z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="9" r="1.6" fill="currentColor" />
  </svg>
);
const IconVoid = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M6.5 6.5l11 11"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

export default Dashboard;
